import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/lib/supabaseClient";
import { clearTenantIdCache } from "@/utils/tenantUtils";

const Impersonate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session } = useAuth();
  const { switchTenant } = useTenant();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleImpersonation = async () => {
      // Prevent multiple executions
      if (loading === false) return;

      try {
        const token = searchParams.get("token");
        const tenantId = searchParams.get("tenant_id");
        const sessionToken = searchParams.get("session_token");
        const refreshToken = searchParams.get("refresh_token");

        if (!token || !tenantId) {
          setError("Invalid impersonation parameters");
          return;
        }

        // Validate the impersonation token with your backend
        const backendUrl =
          import.meta.env.VITE_PYTHON_BACKEND_URL || "http://localhost:8000";
        const response = await fetch(
          `${backendUrl}/api/v1/tenants/${tenantId}/validate-impersonation`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ token }),
          }
        );

        if (!response.ok) {
          setError("Invalid or expired impersonation token");
          return;
        }

        const { user_id, tenant_data } = await response.json();

        // If session tokens were provided, set up the Supabase session
        if (sessionToken && refreshToken) {
          await supabase.auth.setSession({
            access_token: sessionToken,
            refresh_token: refreshToken,
          });

          // Wait for the AuthContext to update
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        // Check if user is now authenticated
        if (!user || !session) {
          const {
            data: { session: currentSession },
          } = await supabase.auth.getSession();
          if (!currentSession) {
            setError("Failed to authenticate. Please try again.");
            return;
          }
          // Wait a bit more for context to update
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        // Since the admin user is authenticated and has superuser privileges,
        // bypass the normal tenant switching and directly call the Supabase RPC

        try {
          // Call the same RPC function that switchTenant uses, but bypass the access check
          const { data: rpcData, error } = await supabase.rpc(
            "set_superadmin_active_tenant",
            { p_tenant_id: tenant_data.tenant_id }
          );


          if (error) {
            console.error("Error setting active tenant:", error);
          }

          // Clear any cached tenant data to force refresh
          clearTenantIdCache();

          // Clear any existing tenant context caches
          localStorage.removeItem("current_tenant_cache");
          sessionStorage.removeItem("current_tenant_cache");

          // Also store the target tenant explicitly for the app to pick up
          localStorage.setItem(
            "target_tenant_override",
            JSON.stringify({
              tenant_id: tenant_data.tenant_id,
              company_name: tenant_data.company_name,
              impersonating: true,
              timestamp: Date.now(),
            })
          );

          // Force a page reload to ensure clean tenant context
          window.location.href = `/app/dashboard?tenant=${tenant_data.tenant_id}&impersonate=true&t=${Date.now()}`;
        } catch (directSwitchError) {
          console.error("Direct tenant switch failed:", directSwitchError);

          // Final fallback: store tenant info and redirect
          localStorage.setItem(
            "target_tenant_override",
            JSON.stringify({
              tenant_id: tenant_data.tenant_id,
              company_name: tenant_data.company_name,
              impersonating: true,
              timestamp: Date.now(),
            })
          );

          window.location.href = `/app/dashboard?tenant=${tenant_data.tenant_id}&impersonate=true&t=${Date.now()}`;
        }
      } catch (err) {
        console.error("Impersonation error:", err);
        setError("Failed to impersonate tenant");
      } finally {
        setLoading(false);
      }
    };

    handleImpersonation();
  }, [searchParams, navigate, user, session, switchTenant]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">Logging you in...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Impersonation Failed
          </h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default Impersonate;
